/**
 * 用户端与后台共用的 API 客户端核心（TECH-BRIEF §3「会话」「错误」、§4；spec-core §1.3、§1.13 H3、§1.17）。
 *
 * - 每个请求带 `X-Nexora: 1`（非 GET 必须，GET 带上无害），cookie 会话（同源，credentials: same-origin）；
 * - 请求与响应都是 JSON；金额一律字符串，客户端不做数值转换；
 * - 失败统一抛 ApiError（code / params 来自 `{ error: { code, params } }`；网络中断为 NETWORK_ERROR，状态码 0）；
 * - 资金接口用 `idempotencyKey`（true 时自动生成 UUID，同一次提交重试必须复用同一个 key，spec-core §1.17）；
 * - `onError` 监听器统一处理 401 / 423 / 只读等全局情况，监听器返回 true 表示已处理（不再走默认处理）。
 * 应用层（apps/web、apps/admin）各自创建实例并配置登录页跳转；这里不依赖 React。
 */
import type { ErrorParamValue, ErrorParams } from '@nexora/shared';

/** 客户端自己的错误码：请求没有到达服务端（断网、DNS、CORS 等）。 */
export const NETWORK_ERROR = 'NETWORK_ERROR';
/** 客户端自己的错误码：请求被调用方中止。 */
export const ABORTED = 'ABORTED';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly params: ErrorParams;

  constructor(status: number, code: string, params: ErrorParams = {}) {
    super(code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.params = params;
  }

  /** VALIDATION_FAILED.params.fields：需要在对应字段旁提示的字段名（D-004、PRD §8.7「表单」）。 */
  get fields(): string[] {
    const fields = this.params.fields;
    return Array.isArray(fields) ? fields.filter((f): f is string => typeof f === 'string') : [];
  }

  /** 4xx 业务错误（重试也不会成功）。 */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

function isParamValue(value: unknown): value is ErrorParamValue {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    (Array.isArray(value) && value.every((v) => typeof v === 'string'))
  );
}

/** 解析统一错误体；不是该格式时返回 null（不引入 zod，保持首屏包体积）。 */
export function parseErrorBody(body: unknown): { code: string; params: ErrorParams } | null {
  if (!body || typeof body !== 'object') return null;
  const error = (body as { error?: unknown }).error;
  if (!error || typeof error !== 'object') return null;
  const { code, params } = error as { code?: unknown; params?: unknown };
  if (typeof code !== 'string' || code.length === 0) return null;
  const safe: Record<string, ErrorParamValue> = {};
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    for (const [key, value] of Object.entries(params as Record<string, unknown>)) if (isParamValue(value)) safe[key] = value;
  }
  return { code, params: safe };
}

export type QueryValue = string | number | boolean | null | undefined;

export interface ApiRequestOptions {
  query?: Readonly<Record<string, QueryValue | readonly QueryValue[]>>;
  /** JSON 请求体（FormData 原样发送，用于上传） */
  body?: unknown;
  headers?: Readonly<Record<string, string>>;
  /** 资金接口的 Idempotency-Key；true 时生成新的 UUID */
  idempotencyKey?: string | true;
  signal?: AbortSignal;
  /** 401 时是否走默认的回到登录页处理（游客也会请求的接口传 false），默认 true */
  authRedirect?: boolean;
  /** 页面关闭时也要发出（埋点、通知点击回传） */
  keepalive?: boolean;
  /** 轮询类请求：后台带 X-Nexora-Activity: passive，不刷新无操作计时（spec-core §1.13 H6） */
  passive?: boolean;
}

export interface ApiErrorContext {
  method: string;
  path: string;
  options: ApiRequestOptions;
}

/** 成功响应的监听：请求信息与解析后的响应体（204 时为 undefined）。 */
export type ApiSuccessListener = (context: ApiErrorContext, data: unknown) => void;

/** 返回 true 表示已处理，其余监听器与默认处理不再执行。 */
export type ApiErrorListener = (error: ApiError, context: ApiErrorContext) => boolean | void;

export interface ApiClientOptions {
  /** 前缀，例如 /api/v1、/admin-api/v1 */
  baseUrl: string;
  fetch?: typeof fetch;
  /** 每个请求附加的动态请求头（例如 X-Nexora-Ctx） */
  headers?: () => Readonly<Record<string, string>>;
  /** 默认的 401 处理（回到登录页）；authRedirect=false 的请求不调用 */
  onUnauthorized?: (error: ApiError, context: ApiErrorContext) => void;
}

export interface ApiClient {
  readonly baseUrl: string;
  request<T>(method: string, path: string, options?: ApiRequestOptions): Promise<T>;
  get<T>(path: string, options?: Omit<ApiRequestOptions, 'body' | 'idempotencyKey'>): Promise<T>;
  post<T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'body'>): Promise<T>;
  put<T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'body'>): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'body'>): Promise<T>;
  delete<T>(path: string, options?: ApiRequestOptions): Promise<T>;
  /** 拼出完整 URL（不发请求），供 <img src>、下载链接等使用 */
  url(path: string, query?: ApiRequestOptions['query']): string;
  onError(listener: ApiErrorListener): () => void;
  /** 成功响应的监听（例如后台在写操作成功后撤下只读提示；用户端按登录响应里的本人 UID 区分表单草稿） */
  onSuccess(listener: ApiSuccessListener): () => void;
}

export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // 兜底（不支持 randomUUID 的环境）：RFC 4122 v4
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function buildQuery(query: ApiRequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, raw] of Object.entries(query)) {
    const values = Array.isArray(raw) ? raw : [raw];
    for (const value of values) if (value !== undefined && value !== null) params.append(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const base = options.baseUrl.replace(/\/+$/, '');
  const listeners = new Set<ApiErrorListener>();
  const successListeners = new Set<ApiSuccessListener>();
  const succeed = (context: ApiErrorContext, data: unknown) => {
    for (const listener of successListeners) listener(context, data);
  };

  const url = (path: string, query?: ApiRequestOptions['query']) => `${base}${path.startsWith('/') ? path : `/${path}`}${buildQuery(query)}`;

  function dispatch(error: ApiError, context: ApiErrorContext): void {
    for (const listener of listeners) {
      if (listener(error, context) === true) return;
    }
    if (error.status === 401 && context.options.authRedirect !== false) options.onUnauthorized?.(error, context);
  }

  async function request<T>(method: string, path: string, opts: ApiRequestOptions = {}): Promise<T> {
    const upper = method.toUpperCase();
    const headers: Record<string, string> = { Accept: 'application/json', 'X-Nexora': '1', ...options.headers?.(), ...opts.headers };
    let body: BodyInit | undefined;
    if (opts.body !== undefined) {
      if (typeof FormData !== 'undefined' && opts.body instanceof FormData) body = opts.body;
      else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(opts.body);
      }
    }
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey === true ? newIdempotencyKey() : opts.idempotencyKey;
    if (opts.passive) headers['X-Nexora-Activity'] = 'passive';
    const context: ApiErrorContext = { method: upper, path, options: opts };
    const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

    let response: Response;
    try {
      response = await doFetch(url(path, opts.query), {
        method: upper,
        headers,
        body,
        credentials: 'same-origin',
        signal: opts.signal,
        keepalive: opts.keepalive,
      });
    } catch (cause) {
      const aborted = opts.signal?.aborted || (cause instanceof DOMException && cause.name === 'AbortError');
      const error = new ApiError(0, aborted ? ABORTED : NETWORK_ERROR);
      if (!aborted) dispatch(error, context);
      throw error;
    }

    if (response.status === 204 || response.status === 205) {
      succeed(context, undefined);
      return undefined as T;
    }
    const text = await response.text();
    let parsed: unknown = undefined;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = undefined;
      }
    }
    if (response.ok) {
      succeed(context, parsed);
      return parsed as T;
    }

    const errBody = parseErrorBody(parsed);
    const error = errBody
      ? new ApiError(response.status, errBody.code, errBody.params)
      : new ApiError(response.status, response.status === 429 ? 'RATE_LIMITED' : 'INTERNAL_ERROR');
    dispatch(error, context);
    throw error;
  }

  return {
    baseUrl: base,
    request,
    get: (path, opts) => request('GET', path, opts),
    post: (path, body, opts) => request('POST', path, { ...opts, body }),
    put: (path, body, opts) => request('PUT', path, { ...opts, body }),
    patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
    delete: (path, opts) => request('DELETE', path, opts),
    url,
    onError(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    onSuccess(listener) {
      successListeners.add(listener);
      return () => successListeners.delete(listener);
    },
  };
}

/** react-query 的重试判定：4xx 业务错误与中止不重试；网络错误与 5xx 最多重试 2 次。 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && (error.isClientError || error.code === ABORTED)) return false;
  return failureCount < 2;
}

export interface ErrorTextDeps {
  /** 词条查找；词条不存在时返回空串或 key 本身（@nexora/i18n 应用层为空串，永不显示词条编号） */
  t: (key: string, vars?: Readonly<Record<string, string | number>>) => string;
  /** 把 ISO 时间格式化成设备时区的本地时刻（例如 14:02，跨日带日期） */
  formatTime: (iso: string) => string;
}

const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * 错误 → 用户可读的一句话（PRD §3.1.3：不显示错误码）。
 * 词条 key 固定为 `error.<CODE>`；params 中的 ISO 时间（retryAt 等）按设备时区换成本地时刻，变量名 `time` 指向 retryAt / resumeAt
 * （REQ-8.2-011"提示可重试的具体时间"）；数组参数不进文案。没有该码的词条时退到 `error.INTERNAL_ERROR`。
 */
export function errorText(error: unknown, deps: ErrorTextDeps): string {
  const api = isApiError(error) ? error : new ApiError(500, 'INTERNAL_ERROR');
  const code = api.code === ABORTED ? NETWORK_ERROR : api.code;
  const vars: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(api.params)) {
    if (typeof value === 'string') vars[key] = ISO_TIME.test(value) ? deps.formatTime(value) : value;
    else if (typeof value === 'number') vars[key] = value;
    else if (typeof value === 'boolean') vars[key] = String(value);
  }
  const at = api.params.retryAt ?? api.params.resumeAt;
  if (typeof at === 'string' && ISO_TIME.test(at)) vars.time = deps.formatTime(at);
  // 词条缺失：@nexora/i18n 应用层返回空串（永不显示词条编号），骨架写法返回 key 本身
  const lookup = (key: string, v?: Record<string, string | number>): string | null => {
    const text = deps.t(key, v);
    return text === '' || text === key ? null : text;
  };
  if (code === 'RATE_LIMITED' && vars.time === undefined) {
    const later = lookup('error.RATE_LIMITED.later');
    if (later) return later;
  }
  const text = lookup(`error.${code}`, vars);
  // 缺少变量值时去掉占位，不把 {name} 显示给用户
  if (text) return /\{\w+\}/.test(text) ? text.replace(/\{\w+\}/g, '').replace(/\s{2,}/g, ' ').trim() : text;
  return lookup('error.INTERNAL_ERROR') ?? '';
}
